import { Link } from 'react-router-dom';
import { ListTodo } from 'lucide-react';
import { routes } from '@/lib/routes';
import { formatRelativeTime } from '@/pages/activity/utils';
import type { DelegatedTask, DelegatedTaskStatus } from '@/types';
import { OverviewPanel } from './OverviewPanel';
import { cleanDisplayText } from '@/utils/display-text';

interface TaskSummaryPanelProps {
  tasks?: DelegatedTask[];
  isLoading?: boolean;
}

const MAX = 10;

type Bucket = 'pending' | 'active' | 'review' | 'completed' | 'failed';

const BUCKET_LABEL: Record<Bucket, string> = {
  pending: 'Pending',
  active: 'Running',
  review: 'Review',
  completed: 'Completed',
  failed: 'Failed',
};

const BUCKET_COLOR: Record<Bucket, string> = {
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  active: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  review: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  failed: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

function bucketize(status: DelegatedTaskStatus): Bucket {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'running':
    case 'active':
      return 'active';
    case 'review':
      return 'review';
    case 'completed':
      return 'completed';
    case 'cancelled':
    case 'failed':
    case 'error':
      return 'failed';
    default:
      return 'completed';
  }
}

function taskTimestamp(task: DelegatedTask): string {
  return task.completedAt || task.updatedAt || task.createdAt || '';
}

export function TaskSummaryPanel({ tasks, isLoading }: TaskSummaryPanelProps) {
  const list = (tasks ?? [])
    .slice()
    .sort((a, b) => taskTimestamp(b).localeCompare(taskTimestamp(a)))
    .slice(0, MAX);

  return (
    <OverviewPanel
      title="Tasks"
      icon={<ListTodo className="h-5 w-5" />}
      action={{ label: 'View all', to: routes.operator.inbox }}
      isLoading={isLoading && !tasks}
      isEmpty={!isLoading && list.length === 0}
      emptyMessage="No tasks yet"
      bodyClassName="p-0"
    >
      <ul className="divide-y divide-border">
        {list.map((task) => {
          const bucket = bucketize(task.status);
          const ts = taskTimestamp(task);
          const target = task.conversationId
            ? routes.operator.chat(task.conversationId)
            : null;
          const row = (
            <div className="flex items-start gap-3 px-4 py-2.5">
              <div className="flex w-20 shrink-0 mt-0.5">
                <span
                  className={
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ' +
                    BUCKET_COLOR[bucket]
                  }
                >
                  {BUCKET_LABEL[bucket]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">
                  {cleanDisplayText(task.title) || 'Untitled task'}
                </p>
                {task.agentName && (
                  <p className="text-xs text-muted-foreground truncate">
                    {task.agentName}
                  </p>
                )}
              </div>
              {ts && (
                <span className="text-xs text-muted-foreground whitespace-nowrap mt-0.5">
                  {formatRelativeTime(ts)}
                </span>
              )}
            </div>
          );
          return (
            <li key={task.taskId} className="hover:bg-accent/40 transition-colors">
              {target ? (
                <Link to={target} className="block">
                  {row}
                </Link>
              ) : (
                row
              )}
            </li>
          );
        })}
      </ul>
    </OverviewPanel>
  );
}
